import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken, HttpHeaders, HttpParams } from '@angular/common/http';

// CDK
import { Dialog } from '@angular/cdk/dialog';

// RxJs
import { debounceTime, delay, distinctUntilChanged, filter, Observable, of, skipWhile, tap, throwError } from 'rxjs';
import { catchError, map, take, takeWhile, skip } from 'rxjs/operators';

// NgRX
import { Store } from '@ngrx/store';
import { AppState } from '@core/store/reducers';
import { selectSessionUserState } from '@core/store/selectors/session.selectors';

// ENVs
import { environment as ENV } from '@environments/environment';

// Providers
import { SecurityService } from '@core/providers/services/security/security.service';

// Utils
import { FormatBase64, looperBase64 } from '@core/utils/functions/looperBase64.util';

// Models
import { Depreciation, DepreciationStatus, History, Step, ToolsErrors } from '@core/models/depreciation.interface';
import { selectDepreciationState } from '@core/store/selectors/depreciation.selectors';
import { User } from '@core/models/session.interface';
import { DepreciationEffectsActions, DepreciationStepperActions } from '@core/store/actions/depreciation.actions';
import { initialState } from '@core/store/reducers/depreciation.reducer';



@Injectable({
  providedIn: 'root'
})
export class DepreciationService {

  private user!: User;
  private depreciation: Depreciation;
  private toolsErrors: any[];


  constructor(
    private securityService: SecurityService,
    private readonly dialog: Dialog,
    private readonly store: Store<AppState>,
    private readonly httpClient: HttpClient
  ) {
    this.depreciation = { } as Depreciation;
    this.toolsErrors = [ ];
  }


  private getSessionUserAndDepreciationState(): void {
    this.store.select(selectSessionUserState).pipe(
      takeWhile(() => !this.user?.email),
    ).subscribe(user => this.user = user!);

    this.store.select(selectDepreciationState).pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
    ).subscribe(depreciation => {
      this.depreciation = depreciation;
      console.log({ depreciation });

      if (!!this.depreciation?.firstStep?.period?.from && !!this.depreciation?.firstStep?.period?.to) {
        this.saveStore().pipe(take(1)).subscribe(({ id, needInjectId }) =>
          needInjectId && this.store.dispatch(DepreciationEffectsActions.injectId({ id }))
        );
      }
    });
  }


  private saveStore(): Observable<any> {
    const bodyDepreciationState: BodyDepreciationState = {
      state: looperBase64(FormatBase64.CODE, this.inhibitToolsFromStepFouth(this.depreciation)),
      ...!!this.depreciation?.id && { id: this.depreciation.id },
      startDate: this.depreciation.firstStep?.period?.from ?? '',
      endDate: this.depreciation.firstStep?.period?.to ?? '',
      step: this.depreciation.currentStep,
      email: this.user?.email ?? '',
      country: 'CL',
      status: this.depreciation.status
    };

    return this.httpClient.post<any>(`${ ENV.URI }${ ENV.DEPRECIATION.STORE.UPDATE }`, bodyDepreciationState).pipe(
      filter(res => !!res),
      map(({ id }) => ({ id, needInjectId: !bodyDepreciationState?.id })),
      catchError(error => {
        this.store.dispatch(DepreciationEffectsActions.injectError({ error }));
        return throwError(error);
      })
    );
  }


  private inhibitToolsFromStepFouth(state: Depreciation): Depreciation {
    return { ...state, fourthStep: { ...state.fourthStep, tabs: { ...state.fourthStep.tabs,
      registered: { ...state.fourthStep.tabs.registered, toolsErrors: { ...initialState.fourthStep.tabs.registered.toolsErrors }},
      unregistered: { ...state.fourthStep.tabs.unregistered, toolsErrors: { ...initialState.fourthStep.tabs.unregistered.toolsErrors }}
    }}};
  }


  public getStore(): Observable<any> {
    this.getSessionUserAndDepreciationState();
    return this.httpClient.get<any>(`${ ENV.URI }${ ENV.DEPRECIATION.STORE.GET }`).pipe(
      filter(({ status }) => ['FINISH', 'FINISH_ERROR', 'IN_PROGRESS', 'CREATED'].includes(status)),
      map(({ state, id, status, step }: any) => ({ depreciation: { ...looperBase64(FormatBase64.DECODE, state), id }, status, step })),
    );
  }

  // First step
  public getStoreInsteandOfInitLostEvents(): Observable<any> {
    return this.httpClient.get<any>(`${ ENV.URI }${ ENV.DEPRECIATION.STORE.GET }`).pipe(
      skipWhile(({ step }) => step > 1),
      map(({ status }: any) => status)
    );
  }


  public getHistory(): Observable<any> {
    return this.httpClient.get<History>(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.FIRST_STEP.HISTORY }`).pipe(
      map(history => history.sort((a, b) => (new Date(b.endDate)).getTime() - (new Date(a.endDate)).getTime())),
    );
  }


  public initLostEvents({ from, to }: { from: string, to: string }): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'text/plain; charset=utf-8' }),
          params = new HttpParams().appendAll({ country: 'CL', idProcess: this.depreciation.id!, from, to });

    return this.httpClient.post(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.FIRST_STEP.LOST_EVENTS_PERIOD }`,
      { }, { headers, responseType: 'text', params }
    );
  }

  // Second step
  public uploadUsefulLife(file: File): Observable<any> {
    const params: HttpParams = new HttpParams().append('country', 'CL'),
          formData = new FormData();

    formData.append('file', file);

    return this.httpClient.post(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.SECOND_STEP.LOAD_SKU }`, formData, { params });
  }

  // Third step
  public uploadCaf(file: File): Observable<any> {
    const params: HttpParams = new HttpParams().appendAll({ idProcess: this.depreciation.id!, country: 'CL' }),
          formData = new FormData();

    formData.append('file', file);

    return this.httpClient.post(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.THIRD_STEP.UPDATE_CAF }`, formData, { params });
  }


  // Fourth step
  public getRegisteredToolsErrors(page: number = 0, size: number = 500): Observable<any> {
    let params = new HttpParams().appendAll({
      country: 'CL', page,
      from: this.depreciation?.firstStep?.period?.from!,
      to: this.depreciation?.firstStep?.period?.to!
    });

    return this.httpClient.get<ToolsErrors>(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.FOURTH_STEP.REGISTERED.GET_TOOLS }`, { params }).pipe(
      tap((res: any) => this.toolsErrors = [...this.toolsErrors, ...res?.toolAltaDtos]),
      map((res: any) => (this.mapRegisteredToolsErrors({
        tools: res?.toolAltaDtos.map((tool: any) => ({
          id: tool?.id,
          sku: tool?.sku,
          storeId: tool?.storeId,
          name: tool?.name,
          code: tool?.codigoProducto,
          serial: tool?.serialNumber,
          error: { type: tool?.errorType, description: tool?.errorDescription }
        })),
        totalMustBeResolved: res?.totalRegisters,
        length: res?.totalRegisters
      }))
    ));
  }


  private mapRegisteredToolsErrors(registeredToolsErrors: any): void {
    let totalReprocessableErrors: number = 0;

    for (const tool of registeredToolsErrors?.tools) {
      switch (tool?.error?.type) {
        case ReprocessableErrors.COSTO_SKU_NOT_FOUND: case ReprocessableErrors.COSTO_OC_NOT_FOUND:
          tool['action'] = 'Reprocesar';
          totalReprocessableErrors += 1;
          break;
        default: tool['action'] = 'Corregir'; break;
      }
    }

    return { ...registeredToolsErrors, totalReprocessableErrors };
  }


  public getUnregisteredToolsErrors(page: number = 0, size: number = 500): Observable<any> {
    let params = new HttpParams().appendAll({
      country: 'CL', page,
      from: this.depreciation?.firstStep?.period?.from!,
      to: this.depreciation?.firstStep?.period?.to!
    });

    return this.httpClient.get<ToolsErrors>(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.FOURTH_STEP.UNREGISTERED.GET_TOOLS }`, { params }).pipe(
      tap((res: any) => this.toolsErrors = [...this.toolsErrors, ...res?.toolBajaDtos]),
      map((res: any) => (this.mapUnregisteredToolsErrors({
        tools: res?.toolBajaDtos.map((tool: any) => ({
          id: tool?.id,
          sku: tool?.sku,
          storeId: tool?.storeId,
          name: tool?.name,
          code: tool?.codigoProducto,
          serial: tool?.serialNumber,
          error: { type: tool?.errorType, description: tool?.errorDescription }
        })),
        totalMustBeResolved: res?.totalErrorRegisters,
        length: res?.totalRegisters
      }))
    ));
  }


  private mapUnregisteredToolsErrors(unregisteredToolsErrors: any): void {
    let totalReprocessableErrors: number = 0

    for (const tool of unregisteredToolsErrors?.tools) {
      switch (tool?.error?.type) {
        case ReprocessableErrors.SKU_NOT_FOUND:
          tool['action'] = 'Reprocesar';
          totalReprocessableErrors += 1;
          break;
        case CorrectableErrors.CAF_NOT_FOUND:
          tool['action'] = 'Corregir';
          break;
        default: tool['action'] = 'Error informativo, no <br /> necesita corrección.'; break;
      }
    }

    return { ...unregisteredToolsErrors, totalReprocessableErrors };
  }


  public reprocessRegisteredTool(id: number): Observable<any> {
    return this.httpClient.post(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.FOURTH_STEP.REGISTERED.REPROCESS_TOOL }`, { id }, {
      responseType: 'text'
    });
  }


  public reprocessUnregisteredTool(id: number): Observable<any> {
    return this.httpClient.post(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.FOURTH_STEP.UNREGISTERED.REPROCESS_TOOL }`, { id }, {
      context: new HttpContext().set(new HttpContextToken<string>(() => 'json'), 'text'),
    });
  }


  public reprocessAllRegisteredTools(ids: number[]): Observable<any> {
    const headers: HttpHeaders = new HttpHeaders({ 'x-tenant-id': 'CL' });
    return this.httpClient.post(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.FOURTH_STEP.REGISTERED.REPROCESS_TOOLS }`, { ids }, { headers });
  }


  public correctRegisteredTool(toolID: number, corrections: any): Observable<any> {
    return this.httpClient.post(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.FOURTH_STEP.REGISTERED.CORRECT_TOOL }`,
      this.mergeCorrections(toolID, corrections)
    );
  }


  public correctUnregisteredTool(toolID: number, corrections: any): Observable<any> {
    return this.httpClient.post(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.FOURTH_STEP.UNREGISTERED.CORRECT_TOOL }`,
      this.mergeCorrections(toolID, corrections)
    );
  }


  private mergeCorrections(toolID: number, corrections: any): any {
    let mapCorrections: any = { };

    corrections?.productCode && (mapCorrections['fixCodigoProducto'] = corrections.productCode);
    corrections?.serviceCode && (mapCorrections['codigoVenta'] = corrections.serviceCode);
    corrections?.usefulLife && (mapCorrections['vidaUtil'] = corrections.usefulLife);
    corrections?.rentalCode && (mapCorrections['sku'] = corrections.rentalCode);
    corrections?.CAFCode && (mapCorrections['caf'] = corrections.CAFCode);

    return { ...this.toolsErrors.find(({ id }) => id === toolID), ...mapCorrections };
  }


  public getSkipEvents(skip: boolean, eventType: any, filter: string, search: string, page: number, limit: number = 5): Observable<any> {
    if (search === '') { filter = 'country'; search = 'CL'; }

    const headers = new HttpHeaders({
      processId: `${ this.depreciation?.id }`,
      country: 'CL',
      skip: `${ skip }`,
      eventType: `${ eventType }`,
      page: `${ --page }`,
      filter: `${ filter }`,
      search: `${ search }`,
      limit: `${ limit }`,
    });

    return this.httpClient.get(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.SKIP_EVENTS_STEP.GET_SKIP_EVENTS }`, { headers }).pipe(
      map(({ body: { payload: { content: tools, totalRegisters: length }}}: any) => ({
        tools: tools?.map((tool: any) => ({
          id: tool?.id,
          name: tool?.name,
          sku: tool?.sku,
          storeId: tool?.storeId,
          code: tool?.codigoProducto,
          serial: tool?.serialNumber,
          type: tool?.saleDate ? 'Baja' : 'Alta',
          action: eventType === '' ? 'Reversar' : 'Omitir'
        })),
        length
      }))
    );
  }


  public setSkipEvent(tool: any, eventType: any): Observable<any> {
    let type: string = '';

    if (eventType === '') {
      type = tool.type.toUpperCase();

    } else { type = eventType; }

    const headers = new HttpHeaders({
      country: 'CL',
      eventId: `${ tool.id }`,
      skip: `${ tool.action === 'Omitir' }`,
      eventType: `${ type }`
    });

    return this.httpClient.put(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.SKIP_EVENTS_STEP.SET_SKIP_EVENT }`, { },
      { headers }
    ).pipe(
      map(({ payload: tool }: any) => ({
        id: tool?.id,
        name: tool?.name,
        sku: tool?.sku,
        storeId: tool?.storeId,
        code: tool?.codigoProducto,
        serial: tool?.serialNumber,
        type: tool?.saleDate ? 'Baja' : 'Alta',
        action: eventType === '' ? 'Reversar' : 'Omitir'
      }))
    );
  }


  public exportSkipEvents(skip: boolean, eventType: any): Observable<any> {
    try {
      window.open(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.SKIP_EVENTS_STEP.EXPORT_SKIP_EVENTS }?processId=${ this.depreciation.id }`
        + `&country=${ 'CL' }&skip=${ skip }&email=${ this.user?.email }&eventType=${ eventType }`, '_blank');

      return of({ status: 'successful' });
    } catch (error) { return of({ error }) }
  }


  public sendReport(): Observable<any> {
    const params = new HttpParams().appendAll({
      idProcess: this.depreciation.id!,
      country: 'CL'
    });

    return this.httpClient.get(`${ ENV.URI }${ ENV.DEPRECIATION.STEPPER.FIFTH_STEP.SEND_REPORTS }`,
      { params , responseType: 'text' }
    );
  }


  public downloadReportFile(fileType: string): void {
    let url: string = `${ ENV.URI }<#>?idProcess=${ this.depreciation.id }&country=${ 'CL' }`;

    switch (fileType) {
      case 'registered': url = url.replace('<#>', ENV.DEPRECIATION.STEPPER.FIFTH_STEP.DOWNLOAD.TOOLS_REGISTERED_REPORT_FILE); break;
      case 'unregistered': url = url.replace('<#>', ENV.DEPRECIATION.STEPPER.FIFTH_STEP.DOWNLOAD.TOOLS_UNREGISTERED_REPORT_FILE); break;
      default: url = url.replace('<#>', ENV.DEPRECIATION.STEPPER.FIFTH_STEP.DOWNLOAD.TOOLS_MASTER_REPORT_FILE); break;
    }

    window.open(url, '_blank');
  }
}



interface BodyDepreciationState {
  id?: number,
  startDate: string,
  endDate: string,
  email: string,
  step: number,
  state: string,
  country: string,
  status?: DepreciationStatus,
}

enum ReprocessableErrors {
  COSTO_SKU_NOT_FOUND = 'COSTO_SKU_NOT_FOUND',
  COSTO_OC_NOT_FOUND = 'COSTO_OC_NOT_FOUND',
  SKU_NOT_FOUND = 'SKU_NOT_FOUND'
}

enum CorrectableErrors {
  VIDA_UTIL_NOT_FOUND = 'VIDA_UTIL_NOT_FOUND',
  SKU_NOT_FOUND = 'SKU_NOT_FOUND',
  CAF_NOT_FOUND = 'CAF_NOT_FOUND'
}
